import { cn } from "@ui/lib";
import type { ReactNode } from "react";

/**
 * The parts of a page written to be quoted: a short answer first, a dated update line, a sample
 * conversation, numbers with their source, and tables. Everything here is plain server-rendered
 * text, because search engines and AI assistants read the HTML, not the structured data.
 */

/** The date a page's facts were last checked, shown on the page and sent as dateModified. */
export function Updated({ date }: { date: string }) {
	return (
		<p className="text-(--mk-muted) text-[14px]">
			Updated{" "}
			<time dateTime={date}>
				{new Date(`${date}T00:00:00Z`).toLocaleDateString("en-GB", {
					day: "numeric",
					month: "long",
					year: "numeric",
					timeZone: "UTC",
				})}
			</time>
		</p>
	);
}

/** "In short": the three-sentence answer a page gives before anything else. */
export function InShort({
	children,
	updated,
}: {
	children: ReactNode;
	updated?: string;
}) {
	return (
		<section aria-label="In short" className="mk-section bg-(--mk-white)">
			<div className="mk-container">
				<div className="mx-auto max-w-3xl rounded-[28px] bg-(--mk-mist) p-7 sm:p-10">
					<p className="mk-kicker text-(--mk-ink-2)">In short</p>
					<div className="mt-3 space-y-3 text-(--mk-ink) text-[18px] leading-relaxed sm:text-[20px]">
						{children}
					</div>
					{updated ? (
						<div className="mt-5">
							<Updated date={updated} />
						</div>
					) : null}
				</div>
			</div>
		</section>
	);
}

/** A run of plain paragraphs and lists under question headings: the body of a guide. */
export function Prose({
	children,
	className,
}: {
	children: ReactNode;
	className?: string;
}) {
	return (
		<div
			className={cn(
				"mk-prose mx-auto max-w-3xl text-(--mk-ink-2) text-[17px] leading-relaxed",
				"[&_h2]:mk-h3 [&_h2]:mt-14 [&_h2]:text-(--mk-ink) [&_h2:first-child]:mt-0",
				"[&_h3]:mt-8 [&_h3]:font-semibold [&_h3]:text-(--mk-ink) [&_h3]:text-[19px]",
				"[&_p]:mt-4 [&_ul]:mt-4 [&_ul]:list-disc [&_ul]:space-y-2 [&_ul]:ps-6",
				"[&_ol]:mt-4 [&_ol]:list-decimal [&_ol]:space-y-2 [&_ol]:ps-6",
				"[&_a]:text-(--mk-ink) [&_a]:underline [&_a]:underline-offset-4",
				"[&_strong]:font-semibold [&_strong]:text-(--mk-ink)",
				className,
			)}
		>
			{children}
		</div>
	);
}

export interface Turn {
	from: "shopper" | "assistant";
	text: string;
	/** The English meaning of a message written in Arabic or Arabizi. */
	en?: string;
	/** What the assistant did at this point (searched, added, asked), in a short line. */
	note?: string;
}

/** A sample conversation, as text: what a shopper wrote and what the assistant did with it. */
export function Conversation({
	turns,
	caption,
}: {
	turns: Turn[];
	caption?: string;
}) {
	return (
		<figure className="mx-auto max-w-2xl rounded-[28px] bg-(--mk-mist) p-5 sm:p-8">
			<ol className="space-y-4">
				{turns.map((turn, i) => (
					<li
						// biome-friendly: turns are static content in a fixed order
						key={`${i}-${turn.text}`}
						className={cn(
							"flex flex-col",
							turn.from === "shopper"
								? "items-end"
								: "items-start",
						)}
					>
						<span className="sr-only">
							{turn.from === "shopper"
								? "Shopper:"
								: "Assistant:"}
						</span>
						<p
							dir="auto"
							className={cn(
								"max-w-[88%] rounded-[20px] px-4 py-2.5 text-[16px] leading-snug",
								turn.from === "shopper"
									? "rounded-br-md bg-(--mk-ink) text-(--mk-white)"
									: "rounded-bl-md bg-(--mk-white) text-(--mk-ink)",
							)}
						>
							{turn.text}
						</p>
						{turn.en ? (
							<p className="mt-1 max-w-[88%] text-(--mk-muted) text-[13px]">
								{turn.en}
							</p>
						) : null}
						{turn.note ? (
							<p className="mt-1 max-w-[88%] text-(--mk-signal-text) text-[13px]">
								{turn.note}
							</p>
						) : null}
					</li>
				))}
			</ol>
			{caption ? (
				<figcaption className="mt-5 text-(--mk-muted) text-[14px]">
					{caption}
				</figcaption>
			) : null}
		</figure>
	);
}

export interface Figure {
	value: string;
	label: string;
	/** Where the number comes from and when it was measured. */
	source: string;
}

/** Numbers a reader can quote, each with where it comes from. */
export function Figures({ items }: { items: Figure[] }) {
	return (
		<dl className="grid gap-x-10 gap-y-10 sm:grid-cols-2 lg:grid-cols-3">
			{items.map((item) => (
				<div
					key={item.label}
					className="mk-reveal border-(--mk-mist-2) border-t pt-5"
				>
					<dt className="font-semibold text-(--mk-ink) text-[34px] tracking-[-0.03em]">
						{item.value}
					</dt>
					<dd className="mt-1 text-(--mk-ink) text-[16px]">
						{item.label}
					</dd>
					<dd className="mt-2 text-(--mk-muted) text-[13px]">
						{item.source}
					</dd>
				</div>
			))}
		</dl>
	);
}

/** A plain comparison table: a header row and one row per point. Scrolls sideways on phones. */
export function CompareTable({
	columns,
	rows,
	caption,
}: {
	columns: string[];
	rows: string[][];
	caption?: string;
}) {
	return (
		<div className="-mx-4 overflow-x-auto px-4">
			<table className="w-full min-w-[640px] border-collapse text-start text-[15px]">
				{caption ? (
					<caption className="pb-4 text-start text-(--mk-muted) text-[14px]">
						{caption}
					</caption>
				) : null}
				<thead>
					<tr>
						{columns.map((column) => (
							<th
								key={column}
								scope="col"
								className="border-(--mk-mist-2) border-b px-3 py-3 text-start font-semibold text-(--mk-ink)"
							>
								{column}
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{rows.map((row) => (
						<tr key={row[0]}>
							{row.map((cell, i) =>
								i === 0 ? (
									<th
										key={`${row[0]}-h`}
										scope="row"
										className="border-(--mk-mist-2) border-b px-3 py-3 text-start align-top font-medium text-(--mk-ink)"
									>
										{cell}
									</th>
								) : (
									<td
										// cells of one row are positional
										key={`${row[0]}-${columns[i]}`}
										className="border-(--mk-mist-2) border-b px-3 py-3 align-top text-(--mk-ink-2)"
									>
										{cell}
									</td>
								),
							)}
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

/** Links to the pages a reader would want next. */
export function RelatedPages({
	links,
}: {
	links: { href: string; label: string }[];
}) {
	return (
		<section
			aria-labelledby="related-title"
			className="mk-section bg-(--mk-white)"
		>
			<div className="mk-container">
				<h2 id="related-title" className="mk-h3">
					Related pages
				</h2>
				<ul className="mt-6 flex flex-wrap gap-3">
					{links.map((link) => (
						<li key={link.href}>
							<a
								href={link.href}
								className="inline-flex rounded-full bg-(--mk-mist) px-4 py-2 text-(--mk-ink) text-[15px] hover:bg-(--mk-mist-2)"
							>
								{link.label}
							</a>
						</li>
					))}
				</ul>
			</div>
		</section>
	);
}
